import { ReactElement, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Box } from "@twilio-paste/core";
import { useTheme } from "@twilio-paste/theme";

import SendingIcon from "../icons/Sending";
import DeliveredIcon from "../icons/Delivered";
import ReadIcon from "../icons/Read";
import FailedIcon from "../icons/Failed";
import MessageActions from "./MessageActions";
import Reactions from "./Reactions";
import { getMember } from "@/redux/selectors/auth";
import ChatContext, { MessageStatus } from "@/contexts/ChatContext";
import { formatUserName } from "@/utils/format";
import UseBrandingAccentColor from "@/hooks/useBrandingAccentColor";
import Autolink from "react-native-autolink";


const statusStyle = {
  display: "inline-block",
  verticalAlign: "middle",
  marginLeft: "4px",
};

const statusIconStyle = {
  marginLeft: "10px",
};

const MessageView = (props) => {
  const theme = useTheme();
  const { message, getStatus, onDeleteMessage } = props;
  const user = useSelector(getMember);
  const { sid, participants, getParticipant } = useContext(ChatContext);
  const userInfo = getParticipant(props.author);
  const accentColor = UseBrandingAccentColor();

  const [status, setStatus] = useState({});

  useEffect(() => {
    getStatus.then((value) => setStatus(value));
  }, [getStatus, message]);

  if (message === undefined) {
    return <></>;
  }

  return (
    <>
      {props.author === user.id.toString() && (
        <>
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              paddingTop: props.topPadding,
              paddingBottom: props.lastMessageBottomPadding,
            }}
          >
            <Box
              style={{
                maxWidth: '50%',
                backgroundColor: accentColor,
                paddingTop: theme.space.space40,
                color: theme.backgroundColors.colorBackgroundBody,
                fontFamily: 'WorkSans_400Regular',
                fontSize: theme.fontSizes.fontSize30,
                fontWeight: theme.fontWeights.fontWeightNormal,
                lineHeight: theme.lineHeights.lineHeight30,
                paddingBottom: theme.space.space40,
                color: theme.backgroundColors.colorBackgroundBody,
                fontSize: theme.fontSizes.fontSize30,
                lineHeight: theme.lineHeights.lineHeight30,
                fontWeight: theme.fontWeights.fontWeightNormal,
                paddingLeft: theme.space.space30,
                paddingRight: theme.space.space30,
                borderRadius: theme.space.space30,
              }}
            >
              {props.message}
              <Box
                style={{
                  display: "flex",
                  flexGrow: 10,
                  paddingTop: theme.space.space30,
                  justifyContent: "space-between",
                }}
              >
                <Box fontSize={theme.fontSizes.fontSize20}>
                  {props.messageTime}
                </Box>
                <Box
                  style={{
                    paddingLeft: theme.space.space30,
                    display: "flex",
                  }}
                >
                  {status[MessageStatus.Delivered] ? (
                    <>
                      <DeliveredIcon
                        color="#fff"
                        style={{ ...statusStyle, ...statusIconStyle }}
                      />
                      {participants[sid]?.length > 2 && (
                        <span style={statusStyle}>
                          {status[MessageStatus.Delivered]}
                        </span>
                      )}
                    </>
                  ) : null}

                  {status[MessageStatus.Sending] ? (
                    <>
                      <SendingIcon
                        color="#fff"
                        style={{ ...statusStyle, ...statusIconStyle }}
                      />
                    </>
                  ) : null}

                  {status[MessageStatus.Failed] ? (
                    <>
                      <FailedIcon
                        color="#fff"
                        style={{ ...statusStyle, ...statusIconStyle }}
                      />
                      {participants[sid]?.length > 2 && (
                        <span style={statusStyle}>
                          {status[MessageStatus.Failed]}
                        </span>
                      )}
                    </>
                  ) : null}

                  {status[MessageStatus.Read] ? (
                    <>
                      <ReadIcon
                        color="#fff"
                        style={{ ...statusStyle, ...statusIconStyle }}
                      />
                      {participants[sid]?.length > 2 && (
                        <span style={statusStyle}>
                          {status[MessageStatus.Read]}
                        </span>
                      )}
                    </>
                  ) : null}

                  <MessageActions
                    messageText={typeof message === "string" ? message : ""}
                    onMessageDelete={onDeleteMessage}
                  />
                </Box>
              </Box>
            </Box>

            {status[MessageStatus.Failed] ? (
              <div style={{ textAlign: "right", paddingTop: "4px" }}>
                <span style={{ color: "#D61F1F" }}>
                  An error has occurred.
                  {/*Tap for more info.*/}
                </span>
              </div>
            ) : null}

            {status[MessageStatus.Delivered] || status[MessageStatus.Read] ? (
              <Reactions
                reactions={props.reactions}
                onReactionsChanged={(reactions) =>
                  props.updateAttributes({ reactions })
                }
              />
            ) : null}
          </Box>
        </>
      )}
      {props.author !== user.id.toString() && (
        <Box
          style={{
            maxWidth: '950px',
            width: '50%',
            display: "flex",
            flexDirection: "column",
            paddingLeft: 10,
            alignItems: "flex-start",
            paddingTop: props.topPadding,
            paddingBottom: props.lastMessageBottomPadding,
          }}
        >
          <Box
            style={{
              color: theme.textColors.colorTextIcon,
              fontFamily: 'WorkSans_400Regular',
              fontSize: theme.fontSizes.fontSize30,
              fontWeight: theme.fontWeights.fontWeightNormal,
              lineHeight: theme.lineHeights.lineHeight30,
            }}
          >
            {props.sameAuthorAsPrev && userInfo && formatUserName(userInfo?.first_name, userInfo?.last_name)}
          </Box>
          <Box
            style={{
              maxWidth: '100%',
              display: "flex",
              flexDirection: "row",
            }}
          >
            <Box
              style={{
                maxWidth: '100%',
                backgroundColor: "lightgrey",
                color: theme.textColors.colorText,
                fontFamily: 'WorkSans_400Regular',
                fontSize: theme.fontSizes.fontSize30,
                fontWeight: theme.fontWeights.fontWeightNormal,
                lineHeight: theme.lineHeights.lineHeight30,
                paddingTop: theme.space.space40,
                paddingBottom: theme.space.space40,
                paddingLeft: theme.space.space30,
                paddingRight: theme.space.space30,
                color: theme.textColors.colorText,
                fontSize: theme.fontSizes.fontSize30,
                lineHeight: theme.lineHeights.lineHeight30,
                fontWeight: theme.fontWeights.fontWeightNormal,
                borderRadius: theme.space.space30,
              }}
            >
              {props.message}
              <Box
                paddingTop="space30"
                fontSize={theme.fontSizes.fontSize20}
                color={theme.textColors.colorTextIcon}
              >
                {props.messageTime}
              </Box>
            </Box>
          </Box>

          <Reactions
            reactions={props.reactions}
            onReactionsChanged={(reactions) =>
              props.updateAttributes({ reactions })
            }
          />
        </Box>
      )}
    </>
  );
};

export default MessageView;